import {
  FaPaperPlane,
  FaStar,
  FaShieldAlt,
  FaSms,
  FaChartLine,
} from "react-icons/fa";
import React, { useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { apiClient } from "../../api";
import { baseUrl } from "../../Environments/environment";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";

export function calculateRequiredReviews(R0, N0, T) {
  if (T >= 5) return 0;
  return Math.ceil(((T - R0) * N0) / (5 - T));
}

export function calculateEstimatedRequests(
  requiredReviews,
  conversionRate = 0.2
) {
  return Math.ceil(requiredReviews / conversionRate);
}

function getTargetReviews(currentRating, reviewCount) {
  const conversionRate = 0.2;
  let diff = 5 - currentRating;
  let t = 0.1;
  let n = 2;
  if (diff >= 1.5) {
    t = 0.5;
  } else if (diff <= 1.2 && diff >= 0.9) {
    t = 0.3;
  } else if (diff < 0.9 && diff >= 0.6) {
    t = 0.2;
  } else if (diff < 0.6 && diff >= 0.3) {
    t = 0.1;
  } else if (diff < 0.3 && diff >= 0.2) {
    n = 2;
  } else if (diff < 0.2 && diff >= 0.1) {
    n = 1;
  }
  const ans = [];
  for (let i = 1; i <= n; i++) {
    const rating = currentRating + t * i;
    const requiredReviews = calculateRequiredReviews(
      currentRating,
      reviewCount,
      rating
    );
    const estimatedRequests = calculateEstimatedRequests(
      requiredReviews,
      conversionRate
    );
    ans.push({
      requiredReviews,
      action: `Sent another ${estimatedRequests} review links`,
      rating,
      increase: t * i,
      status: "Upcoming",
    });
  }
  return ans;
}

export default function ReviewDashboard() {
  const { selectedPropertyId } = useProperty();

  const [isLoading, setIsLoading] = useState(false);
  const [negativeReviews, setNegativeReviews] = useState(0);
  const [positiveReviews, setPositiveReviews] = useState(0);
  const [totalLinkClicks, setTotalLinkClicks] = useState(0);
  const [totalReviewsCount, setTotalReviewsCount] = useState(0);
  const [totalSmsDelivered, setTotalSmsDelivered] = useState(0);
  const [totalSmsSent, setTotalSmsSent] = useState(0);

  const [currentRating, setCurrentRating] = useState(3.2);
  const [currentReviewCount, setCurrentReviewCount] = useState(100);
  const [intialRating, setInitialRating] = useState(3.0);
  const [initialReviewCount, setInitialReviewCount] = useState(80);
  const [milestones, setMilestones] = useState([]);

  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("accessToken");
      const response = await apiClient.get(
        `${baseUrl}/reviews/metrics/${selectedPropertyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log(response?.data?.result);
      const d = response?.data?.result;
      if (d) {
        setNegativeReviews(d.negativeReviews);
        setPositiveReviews(d.positiveReviews);
        setTotalLinkClicks(d.totalLinkClicks);
        setTotalReviewsCount(d.totalReviewsCount);
        setTotalSmsDelivered(d.totalSmsDelivered);
        setTotalSmsSent(d.totalSmsSent);
        let x = getTargetReviews(currentRating, currentReviewCount);

        setMilestones([
          {
            rating: intialRating,

            increase: "0.0",
            action: "Started campaign",
            status: "start",
          },
          {
            rating: currentRating,

            increase: (currentRating - intialRating).toFixed(1),
            action: "Current Performance",
            status: "current",
          },
          ...x,
        ]);
      }
    } catch (error) {
      console.error("Error fetching", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchMetrics();
  }, []);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {isLoading && <LoadingSpinner />}

      {/* ================= KPIs ================= */}
      <h2 className="text-xl font-medium mb-4 flex items-center gap-2 pl-2">
        <FaChartLine className="text-blue-600" /> Key Metrics Overview
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <KpiCard
          title="Review Requests Sent"
          value={totalSmsSent}
          icon={<FaPaperPlane />}
          color="text-blue-600"
          sub="Links Sent"
          bgcolor="bg-blue-50 p-2 rounded-lg"
        />

        <KpiCard
          title="Review Conversion"
          value={`${(
            ((positiveReviews + negativeReviews) * 100) / totalReviewsCount || 1
          ).toFixed(2)} %`}
          sub="Links clicked"
          icon={<FaChartLine />}
          color="text-purple-600"
          bgcolor="bg-purple-50 p-2 rounded-lg"
        />

        <KpiCard
          title="Positive Reviews"
          value={positiveReviews}
          icon={<FaStar />}
          color="text-orange-500"
          bgcolor="bg-orange-50 p-2 rounded-lg"
        />

        <KpiCard
          title="Negative Reviews Blocked"
          value={negativeReviews}
          icon={<FaShieldAlt />}
          color="text-red-500"
          bgcolor="bg-red-50 p-2 rounded-lg"
        />

        <KpiCard
          title="SMS Delivery Rate"
          value={`${((totalSmsDelivered * 100) / totalSmsSent || 1).toFixed(
            2
          )} %`}
          sub="Via Twilio"
          icon={<FaSms />}
          color="text-green-600"
          bgcolor="bg-green-50 p-2 rounded-lg"
        />
      </div>

      <div className="bg-white p-5 rounded-xl shadow">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-medium text-lg flex items-center gap-2">
            <FaStar className="text-yellow-500" /> Rating Progress & Milestones
          </h3>
          {/* <div className="flex ">
            <div className="pl-3 flex items-center ">
              <span className="bg-green-100 text-green-700 text-sm px-3 py-1 rounded-full">
                +{1.5} stars ({46.8}% increase)
              </span>
            </div>
          </div> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {milestones.map((m, i) => (
            <MilestoneCard key={i} data={m} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ================= KPI CARD ================= */

function KpiCard({ title, value, sub, icon, color, bgcolor }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow flex justify-between items-center hover:shadow-md transition-shadow">
      <div>
        <p className="text-gray-500 text-sm">{title}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
      <div className={` text-xl ${color} ${bgcolor}`}>{icon}</div>
    </div>
  );
}

/* ================= MILESTONE CARD ================= */

function MilestoneCard({ data }) {
  const isCurrent = data.status === "current";

  return (
    <div
      className={`rounded-xl p-4 shadow border hover:shadow-md transition-shadow min-h-[180px]
        ${isCurrent ? "bg-green-50 border-green-400" : "bg-white"}
      `}
    >
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-gray-500">{data.month}</span>
        {isCurrent ? (
          <span className="bg-green-200 text-green-800 text-xs px-2 py-1 rounded-full">
            Current
          </span>
        ) : (
          <span className="bg-orange-100 text-orange-600 text-xs px-2 py-1 rounded-full">
            {data.status}
          </span>
        )}
      </div>

      <p className="text-2xl font-bold text-yellow-600 flex items-center gap-1">
        ⭐ {data.rating}
      </p>
      {data.requiredReviews && (
        <p className="text-xs text-gray-500 mb-2">
          {data.requiredReviews} Five Star Rating ★ Needed
        </p>
      )}

      <p className="text-green-600 text-sm mb-2 mt-2">↗ +{data.increase}</p>

      <p className="text-xs text-blue-600 flex items-center gap-1">
        🚀 {data.action}
      </p>
    </div>
  );
}
